import { useContext, useMemo } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { useJnx, JnxExpr } from '../../../util/jnx';
import getPathFromId from '../../../util/getPathFromId';
import { useSideChannelSubscription } from '../../../util/useSideChannel';
import useOptionsLookup, { LookupObject } from '../hooks/useOptionsLookup';

export type ScheduleLookup = LookupObject & { id?: JnxExpr<string>; label?: JnxExpr<string> };
export type ScheduleOption = { value: string; label: string };

type FormContextLike = { sideChannel?: any } | undefined;

/**
 * Loads the schedule picker's options from the array's own `items.properties.schedule.lookup` block, the same way
 * `LookupFormField` does: `id` and `label` are jsonata expressions evaluated per fetched item.
 */
export default function useScheduleOptions(lookup: ScheduleLookup | undefined, formContext: FormContextLike, fieldId: string) {
  const auth = useContext(AuthContext);
  const [rootFormData, formObject] = useSideChannelSubscription(formContext?.sideChannel, [0, 1]) || [{}, {}];
  const path = useMemo(() => getPathFromId(fieldId), [fieldId]);

  const { options, loadingOptions, errorLoadingOptions } = useOptionsLookup({
    lookup: lookup as LookupObject,
    rootFormData,
    path,
    auth,
    formContext: formContext as Object,
    formObject,
  });

  const idJnx = useJnx(lookup?.id);
  const labelJnx = useJnx(lookup?.label);

  const scheduleOptions = useMemo<ScheduleOption[]>(
    () =>
      (options ?? []).map((item: any) => {
        const value = String(idJnx ? idJnx.eval(item) : item?.id);
        const label = labelJnx ? labelJnx.eval(item) : item?.label;

        return { value, label: label == null ? value : String(label) };
      }),
    [options, idJnx, labelJnx]
  );

  // The lookup refetches (from cache) whenever formContext changes; only show loading before the first result.
  return { options: scheduleOptions, loading: !!loadingOptions && !options, error: errorLoadingOptions };
}
